import type { QueryClient } from "@tanstack/react-query";

import type { FilterOptionsResolver } from "./FilterDefinition";
import type { SuggestionsProvider } from "./suggestions";

/**
 * A {@link SuggestionsProvider} as `OptionsValueInput`'s option source: the
 * props to spread onto the picker.
 */
export type SuggestionOptions =
  | {
      options: string[];
      hasUnset?: boolean;
      counts?: Readonly<Record<string, number>>;
      unsetCount?: number;
      optionsKey?: never;
      minSearchLength?: never;
    }
  | {
      options: FilterOptionsResolver;
      optionsKey: string;
      minSearchLength: number;
    };

/**
 * Adapts a suggestion source to the picker's option source.
 *
 * - A `list` provider is a static list, matched client-side by the picker as
 *   the provider would match it, with its `hasUnset`.
 * - A `fetch` provider is a resolver named by the provider's key. Its pages
 *   go through the app query client, cached per `(key, search)` as the
 *   provider contract allows, so fields sharing a source share pages and
 *   reopening a field costs no request — the cross-mount caching the picker
 *   leaves to resolvers (see `useAuditIdentityOptions`). The resolver drops
 *   the picker's `AbortSignal` for the query client's: a cached page is
 *   shared, so one picker's superseded search must not abort a fetch another
 *   still awaits.
 * - No provider is an empty list: the picker then offers only the selection
 *   and whatever is typed.
 */
export function suggestionOptions(
  provider: SuggestionsProvider | undefined,
  queryClient: QueryClient,
): SuggestionOptions {
  if (provider === undefined) {
    return { options: [] };
  }
  if (provider.kind === "list") {
    const source: SuggestionOptions = { options: [...provider.values] };
    if (provider.hasUnset !== undefined) {
      source.hasUnset = provider.hasUnset;
    }
    if (provider.counts !== undefined) {
      source.counts = provider.counts;
    }
    if (provider.unsetCount !== undefined) {
      source.unsetCount = provider.unsetCount;
    }
    return source;
  }
  return {
    options: async (search) => {
      const page = await queryClient.ensureQueryData({
        queryKey: ["filter-suggestions", provider.key, search],
        queryFn: ({ signal }) => provider.fetch(search, signal),
      });
      return {
        options: page.values,
        truncated: page.truncated,
        hasUnset: page.hasUnset,
        counts: page.counts,
        unsetCount: page.unsetCount,
      };
    },
    optionsKey: provider.key,
    minSearchLength: provider.minSearchLength ?? 0,
  };
}
